import {
   MAX_WEIGHT_ENTRIES_PER_REQUEST,
   weightListResponseSchema,
   weightUpsertResponseSchema,
   type WeightEntry,
   type WeightListResponse,
   type WeightUpsertResponse,
} from '@fasting-timer/shared'
import { safeParse } from 'valibot'

import { readErrorMessage } from './measurementsApi'

type WeightsApiDeps = {
   weightsEndpoint: string
   canUseRemoteSync: () => boolean
   getUserId: () => string
   resolveSyncUserId: () => Promise<string>
}

/**
 * Creates the `/v1/weights` client: delta listing and batch upsert. Deletes travel
 * as tombstones through the upsert, so no separate call is needed.
 *
 * @param deps - Injected endpoint and user/sync accessors.
 * @returns The API methods.
 */
export function createWeightsApi(deps: WeightsApiDeps) {
   /**
    * Resolves the hashed user id when a sync key is set and remote sync is usable.
    *
    * @returns The hashed id, or null when remote calls are not allowed.
    */
   async function resolveRemoteUserId() {
      if (!deps.getUserId() || !deps.canUseRemoteSync()) {
         return null
      }
      return (await deps.resolveSyncUserId()) || null
   }

   /**
    * Lists entries, optionally only those the server stored after `since`.
    *
    * @param options - `since` ISO cursor from a previous response's `serverTime`.
    * @returns The parsed response, or null when remote sync is unavailable.
    */
   async function list(options: { since?: string | null } = {}) {
      const hashedId = await resolveRemoteUserId()
      if (!hashedId) {
         return null
      }
      const params = new URLSearchParams({ userId: hashedId })
      if (options.since) {
         params.set('since', options.since)
      }
      const response = await fetch(`${deps.weightsEndpoint}?${params.toString()}`)
      if (!response.ok) {
         throw new Error(
            await readErrorMessage(response, `Weights fetch failed (${response.status})`),
         )
      }
      const parsed = safeParse(weightListResponseSchema, await response.json())
      if (!parsed.success) {
         throw new Error('Weights response was malformed.')
      }
      return parsed.output satisfies WeightListResponse
   }

   /**
    * Upserts entries in batches of the server's maximum request size.
    *
    * @param entries - The entries to push.
    * @returns The parsed responses (one per batch), or null when remote sync is unavailable.
    */
   async function upsert(entries: readonly WeightEntry[]) {
      if (entries.length === 0) {
         return null
      }
      const hashedId = await resolveRemoteUserId()
      if (!hashedId) {
         return null
      }
      const responses: WeightUpsertResponse[] = []
      for (
         let index = 0;
         index < entries.length;
         index += MAX_WEIGHT_ENTRIES_PER_REQUEST
      ) {
         const batch = entries.slice(index, index + MAX_WEIGHT_ENTRIES_PER_REQUEST)
         const response = await fetch(deps.weightsEndpoint, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: hashedId, entries: batch }),
         })
         if (!response.ok) {
            throw new Error(
               await readErrorMessage(
                  response,
                  `Weights push failed (${response.status})`,
               ),
            )
         }
         const parsed = safeParse(weightUpsertResponseSchema, await response.json())
         if (!parsed.success) {
            throw new Error('Weights push response was malformed.')
         }
         responses.push(parsed.output)
      }
      return responses
   }

   return { list, upsert }
}

export type WeightsApi = ReturnType<typeof createWeightsApi>
